/**
 * Free-text duties — the "Non-counter deployments" options of every counter dropdown.
 *
 * A duty is working time spent on something that is not a counter of this car: "SOS" (the Ops room has not
 * said which car yet), "SOS DCAR", "B@M CTRL BOOTH". The text is written into the officer's own row and
 * saved with the roster (RosterState.duty_slots), so everyone in the session reads it; it never reaches the
 * counter grid's counters, the stats, No Relief or auto-assign.
 *
 * Which options the dropdowns offer — and which get a row of their own under the counters (on for the
 * booth) — belongs to the SESSION, not the browser: everyone working the same session ID sees and may edit
 * the same list (RosterState.duty_options, edited under Settings → Non-counter deployments). It rides along with the
 * roster state, like the WhatsApp footer (utils/whatsappFooter).
 */
import { useSyncExternalStore } from 'react';
import { apiClient } from '@/api/client';
import type { DutyOption } from '@/types/api';

export type { DutyOption };

/** The longest duty the server takes (backend/config.py: MAX_DUTY_LENGTH). */
export const MAX_DUTY_LENGTH = 60;

/** What a session offers until someone edits the list — the same as backend/config.py: DEFAULT_DUTY_OPTIONS. */
export const DEFAULT_DUTY_OPTIONS: DutyOption[] = [
  { text: 'SOS', show_row: false },
  { text: 'SOS ACAR', show_row: false },
  { text: 'SOS DCAR', show_row: false },
  { text: 'FLOATER / SOS', show_row: false },
  { text: 'B@M CTRL BOOTH', show_row: true },
];

/** A duty as the server stores it: one line, single-spaced, upper case, at most MAX_DUTY_LENGTH. */
export function normalizeDuty(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toUpperCase().slice(0, MAX_DUTY_LENGTH);
}

let current: DutyOption[] = DEFAULT_DUTY_OPTIONS;
const listeners = new Set<() => void>();

const sameList = (a: DutyOption[], b: DutyOption[]) =>
  a.length === b.length && a.every((o, i) => o.text === b[i].text && o.show_row === b[i].show_row);

function publish(next: DutyOption[]): void {
  if (sameList(current, next)) return;
  current = next;
  listeners.forEach((l) => l());
}

/** Adopt the list that came back with a roster-state payload. */
export function adoptDutyOptions(value: DutyOption[] | undefined): void {
  if (value === undefined) return;
  publish(value);
}

/** Forget what we know — call when the session changes. */
export function resetDutyOptions(): void {
  publish(DEFAULT_DUTY_OPTIONS);
}

/**
 * Save a new list for the whole session. Shown at once here; the server then tells every other tab, and
 * ours is put back to what the server holds if the save fails. `clearRemoved`: also take everyone off the
 * duties the new list drops — without it the server refuses while somebody is on one.
 */
export async function saveDutyOptions(next: DutyOption[], clearRemoved = false): Promise<void> {
  const before = current;
  publish(next);
  try {
    await apiClient.setDutyOptions(next, clearRemoved);
  } catch (e) {
    publish(before);
    throw e;
  }
}

export function getDutyOptions(): DutyOption[] {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** This session's duty options, re-rendering the caller when they change. */
export function useDutyOptions(): DutyOption[] {
  return useSyncExternalStore(subscribe, getDutyOptions, () => DEFAULT_DUTY_OPTIONS);
}

/** Who is on one non-counter deployment right now, and for how many 15-minute slots in all. */
export interface DutyUse {
  /** Each officer once, by name when the roster has one, else by key. */
  officers: string[];
  slots: number;
}

/** Per duty text, who is on it — what removing it from the list would leave behind. */
export function dutyUsage(
  dutySlots: Record<string, Record<string, string>> | undefined,
  officerMetadata: Record<string, { name?: string; deployment?: string }> | undefined,
): Record<string, DutyUse> {
  const out: Record<string, DutyUse> = {};
  for (const [officerKey, slots] of Object.entries(dutySlots ?? {})) {
    const meta = officerMetadata?.[officerKey];
    const who = [meta?.deployment, meta?.name].filter(Boolean).join(' - ') || officerKey;
    for (const text of Object.values(slots)) {
      const use = (out[text] ??= { officers: [], slots: 0 });
      use.slots += 1;
      if (!use.officers.includes(who)) use.officers.push(who);
    }
  }
  return out;
}

/**
 * The duties that get a row of their own under the counters, in the order the session listed them — and,
 * after them, any booth the roster has that the list does not (a duty from before the list was edited is
 * otherwise just text on the officer's row).
 */
export function dutyRowLabels(options: DutyOption[], present: Iterable<string>): string[] {
  const rows = options.filter((o) => o.show_row).map((o) => o.text);
  const listed = new Set(options.map((o) => o.text));
  for (const text of present) {
    if (!listed.has(text) && text.startsWith('B@M') && !rows.includes(text)) rows.push(text);
  }
  return rows;
}
